import React, { useEffect, useState } from "react";
import api, { fileDownloadUrl, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import UploadButton from "@/components/UploadButton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  X, Send, Plus, Trash2, Paperclip, CheckCircle2, Circle, History, MessageSquare,
  ShieldCheck, RotateCcw, Save, FileText,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const PRIO_CLR = { low: "bg-slate-400", medium: "bg-blue-500", high: "bg-amber-500", urgent: "bg-red-500" };
const uid = () => Math.random().toString(36).slice(2, 10);
const RC_WEEKDAYS = [
  { code: "mon", k: "wdMon" }, { code: "tue", k: "wdTue" }, { code: "wed", k: "wdWed" },
  { code: "thu", k: "wdThu" }, { code: "fri", k: "wdFri" }, { code: "sat", k: "wdSat" }, { code: "sun", k: "wdSun" },
];

export default function TicketDialog({ ticketId, meta, open, onOpenChange, onChanged, canEdit, canComplete }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState([]);
  const [tab, setTab] = useState("comments");
  const [closing, setClosing] = useState(false);
  const [proof, setProof] = useState({ file_id: null, name: null, note: "" });

  const uname = (id) => meta.users.find((u) => u.id === id)?.name || "—";
  const uavatar = (id) => meta.users.find((u) => u.id === id)?.profile_picture;
  const cname = (id) => meta.clients.find((c) => c.id === id)?.name || "—";

  const load = () => api.get(`/tickets/${ticketId}`).then((r) => { setData(r.data); setForm(r.data.ticket); });
  useEffect(() => { if (ticketId && open) { setTab("comments"); load(); } }, [ticketId, open]); // eslint-disable-line

  if (!open || !form) return null;
  const t0 = form;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.put(`/tickets/${ticketId}`, {
        title: t0.title, description: t0.description, ri_client_id: t0.ri_client_id,
        coordinator_id: t0.coordinator_id, emanager_id: t0.emanager_id, director_id: t0.director_id,
        assigned_employee_ids: t0.assigned_employee_ids, priority: t0.priority, status: t0.status,
        due_date: t0.due_date, labels: t0.labels, checklist: t0.checklist, internal_notes: t0.internal_notes,
        is_recurring: !!t0.is_recurring, recurrence: t0.recurrence || "weekly",
        recurrence_days: t0.recurrence_days || [], recurrence_time: t0.recurrence_time || "08:00",
        recurrence_dom: t0.recurrence_dom || 1,
      });
      toast.success(t("save")); onChanged(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tickets/${ticketId}/comments`, { text: comment, mentions });
    setComment(""); setMentions([]); load(); onChanged();
  };

  const toggleChecklist = (id) => set("checklist", t0.checklist.map((c) => c.id === id ? { ...c, done: !c.done } : c));
  const addChecklist = () => set("checklist", [...(t0.checklist || []), { id: uid(), text: "", done: false }]);

  const doClose = async () => {
    await api.post(`/tickets/${ticketId}/close`, { proof: proof.file_id ? { file_id: proof.file_id, name: proof.name } : null, note: proof.note });
    setClosing(false); setProof({ file_id: null, name: null, note: "" }); toast.success(t("statusDone")); load(); onChanged();
  };
  const reopen = async () => { await api.post(`/tickets/${ticketId}/reopen`); load(); onChanged(); };

  const toggleAssignee = (id) => set("assigned_employee_ids", (t0.assigned_employee_ids || []).includes(id)
    ? t0.assigned_employee_ids.filter((x) => x !== id) : [...(t0.assigned_employee_ids || []), id]);
  const toggleMention = (id) => setMentions((m) => m.includes(id) ? m.filter((x) => x !== id) : [...m, id]);

  const done = t0.status === "done";
  const employees = meta.users;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 max-h-[92vh] overflow-hidden flex flex-col" data-testid="ticket-dialog">
        <DialogTitle className="sr-only">{t0.number} — {t0.title}</DialogTitle>
        <DialogDescription className="sr-only">{t("tickets")}</DialogDescription>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-[11px]">{t0.number}</Badge>
              <span className={`w-2 h-2 rounded-full ${PRIO_CLR[t0.priority]}`} />
              <span className="text-xs text-muted-foreground capitalize">{t("priority" + t0.priority.charAt(0).toUpperCase() + t0.priority.slice(1))}</span>
              {done && <Badge className="bg-emerald-500 text-[10px]">{t("statusDone")}</Badge>}
            </div>
            <Input value={t0.title} disabled={!canEdit} onChange={(e) => set("title", e.target.value)} className="text-lg font-semibold border-none px-0 focus-visible:ring-0 h-auto" data-testid="ticket-title-input" />
          </div>
          <div className="flex items-center gap-1.5">
            {!t0.is_template && (canEdit || canComplete) && (done
              ? <Button size="sm" variant="outline" onClick={reopen} data-testid="reopen-ticket"><RotateCcw className="w-4 h-4 mr-1" />{t("reopenTicket")}</Button>
              : <Button size="sm" onClick={() => setClosing(true)} data-testid="close-ticket-btn" className="bg-emerald-600 hover:bg-emerald-700"><ShieldCheck className="w-4 h-4 mr-1" />{t("closeTicket")}</Button>)}
            <button onClick={() => onOpenChange(false)} className="w-8 h-8 rounded-lg hover:bg-accent flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin grid grid-cols-1 md:grid-cols-[1.6fr_1fr]">
          {/* Left: content */}
          <div className="p-5 space-y-5 md:border-r border-border">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("description")}</Label>
              <Textarea rows={3} value={t0.description || ""} disabled={!canEdit} onChange={(e) => set("description", e.target.value)} data-testid="ticket-description" />
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />{t("checklist")}</Label>
              {(t0.checklist || []).map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <button onClick={() => canEdit && toggleChecklist(c.id)} data-testid="checklist-toggle">{c.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-muted-foreground" />}</button>
                  <Input value={c.text} disabled={!canEdit} onChange={(e) => set("checklist", t0.checklist.map((x) => x.id === c.id ? { ...x, text: e.target.value } : x))} className={`h-8 ${c.done ? "line-through text-muted-foreground" : ""}`} />
                  {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => set("checklist", t0.checklist.filter((x) => x.id !== c.id))}><X className="w-3.5 h-3.5" /></Button>}
                </div>
              ))}
              {canEdit && <Button variant="outline" size="sm" onClick={addChecklist} data-testid="add-checklist"><Plus className="w-3.5 h-3.5 mr-1" />{t("addChecklistItem")}</Button>}
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" />{t("attachments")}</Label>
              <div className="flex flex-wrap gap-1.5">
                {(t0.attachments || []).map((a, i) => (
                  <a key={i} href={fileDownloadUrl(a.file_id)} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-muted flex items-center gap-1"><FileText className="w-3 h-3" />{a.name}</a>
                ))}
              </div>
              {canEdit && <UploadButton label={t("attachments")} accept="*" category="ticket" testId="upload-attachment" onUploaded={(d) => set("attachments", [...(t0.attachments || []), { file_id: d.id, name: d.original_filename }])} />}
            </div>

            {t0.final_proof && (
              <div className="p-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 space-y-1">
                <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />{t("finalProof")}</p>
                {t0.final_proof.note && <p className="text-sm">{t0.final_proof.note}</p>}
                {t0.final_proof.file_id && <a href={fileDownloadUrl(t0.final_proof.file_id)} target="_blank" rel="noreferrer" className="text-xs text-primary underline">{t0.final_proof.name}</a>}
              </div>
            )}

            {/* Comments / Activity tabs */}
            <div>
              <div className="flex gap-1 border-b border-border mb-3">
                <button onClick={() => setTab("comments")} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${tab === "comments" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-comments"><MessageSquare className="w-3.5 h-3.5" />{t("comments")}</button>
                <button onClick={() => setTab("activity")} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${tab === "activity" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-activity"><History className="w-3.5 h-3.5" />{t("activity")}</button>
              </div>
              {tab === "comments" ? (
                <div className="space-y-3">
                  {data.comments.map((c) => (
                    <div key={c.id} className="flex gap-2" data-testid="ticket-comment">
                      <UserAvatar name={c.user_name} src={c.user_avatar} size={30} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs"><b>{c.user_name}</b> <span className="text-muted-foreground">{c.created_at?.slice(0, 16).replace("T", " ")}</span></p>
                        <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                        {c.mentions?.length > 0 && <p className="text-[11px] text-primary">@ {c.mentions.map(uname).join(", ")}</p>}
                      </div>
                    </div>
                  ))}
                  {data.comments.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
                  <div className="space-y-2 pt-2">
                    <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("addComment")} data-testid="comment-input" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select onValueChange={toggleMention}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid="mention-select"><SelectValue placeholder={`@ ${t("mention")}`} /></SelectTrigger>
                        <SelectContent>{employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                      {mentions.map((m) => <Badge key={m} variant="secondary" className="gap-1 text-[10px]">@{uname(m)}<button onClick={() => toggleMention(m)}>×</button></Badge>)}
                      <Button size="sm" className="ml-auto" onClick={addComment} data-testid="send-comment"><Send className="w-3.5 h-3.5 mr-1" />{t("addComment")}</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.activity.map((a) => (
                    <div key={a.id} className="text-xs flex gap-2" data-testid="activity-item">
                      <span className="text-muted-foreground w-28 shrink-0">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                      <span><b>{a.user_name}</b> {a.action} {a.detail && <span className="text-muted-foreground">— {a.detail}</span>}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: meta */}
          <div className="p-5 space-y-3 bg-muted/20">
            <Meta label={t("riClient")}>
              <Select value={t0.ri_client_id || ""} disabled={!canEdit} onValueChange={(v) => set("ri_client_id", v)}>
                <SelectTrigger className="h-9" data-testid="ticket-client"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{meta.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Meta>
            <Meta label={t("priority")}>
              <Select value={t0.priority} disabled={!canEdit} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="h-9" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t("priority" + p.charAt(0).toUpperCase() + p.slice(1))}</SelectItem>)}</SelectContent>
              </Select>
            </Meta>
            <Meta label={t("dueDate")}><Input type="date" className="h-9" disabled={!canEdit} value={t0.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="ticket-due" /></Meta>
            <Meta label={t("recurring")}>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" disabled={!canEdit} checked={!!t0.is_recurring} onChange={(e) => set("is_recurring", e.target.checked)} className="w-4 h-4" data-testid="ticket-recurring" />
                  <span className="text-xs text-muted-foreground">{t0.is_recurring ? t("recurring") : t("oneTime")}</span>
                </label>
                {t0.is_recurring && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={t0.recurrence || "weekly"} disabled={!canEdit} onValueChange={(v) => set("recurrence", v)}>
                        <SelectTrigger className="h-8" data-testid="ticket-recurrence"><SelectValue /></SelectTrigger>
                        <SelectContent>{["daily", "weekly", "monthly"].map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="time" disabled={!canEdit} value={t0.recurrence_time || "08:00"} onChange={(e) => set("recurrence_time", e.target.value)} className="h-8" data-testid="ticket-time" />
                    </div>
                    {t0.recurrence === "weekly" && (
                      <div className="flex flex-wrap gap-1" data-testid="ticket-days">
                        {RC_WEEKDAYS.map((d) => {
                          const on = (t0.recurrence_days || []).includes(d.code);
                          return <button type="button" key={d.code} disabled={!canEdit} onClick={() => set("recurrence_days", on ? (t0.recurrence_days || []).filter((c) => c !== d.code) : [...(t0.recurrence_days || []), d.code])} className={`px-2 py-0.5 rounded-full text-[11px] border ${on ? "bg-primary text-white border-primary" : "border-border"}`}>{t(d.k)}</button>;
                        })}
                      </div>
                    )}
                    {t0.recurrence === "monthly" && (
                      <div className="flex items-center gap-2 text-xs"><span>{t("dayOfMonth")}</span><Input type="number" min="1" max="31" disabled={!canEdit} value={t0.recurrence_dom || 1} onChange={(e) => set("recurrence_dom", Number(e.target.value))} className="h-8 w-16" /></div>
                    )}
                    <p className="text-[10px] text-muted-foreground">{t("recurringHint")}</p>
                  </div>
                )}
              </div>
            </Meta>
            <Meta label={t("coordinator")}>{personSelect(t0.coordinator_id, (v) => set("coordinator_id", v), employees, canEdit, "ticket-coordinator")}</Meta>
            <Meta label={t("emanager")}>{personSelect(t0.emanager_id, (v) => set("emanager_id", v), employees, canEdit, "ticket-emanager")}</Meta>
            <Meta label={t("director")}>{personSelect(t0.director_id, (v) => set("director_id", v), employees, canEdit, "ticket-director")}</Meta>
            <Meta label={t("assignedEmployees")}>
              <div className="flex flex-wrap gap-1">
                {employees.map((u) => {
                  const on = (t0.assigned_employee_ids || []).includes(u.id);
                  return <button key={u.id} disabled={!canEdit} onClick={() => toggleAssignee(u.id)} data-testid="assignee-toggle"
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-accent"}`}>{u.name}</button>;
                })}
              </div>
            </Meta>
            <Meta label={t("labels")}>
              <Input className="h-9" disabled={!canEdit} value={(t0.labels || []).join(", ")} onChange={(e) => set("labels", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="ticket-labels" />
            </Meta>
            <Meta label={t("internalNotes")}><Textarea rows={2} disabled={!canEdit} value={t0.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} /></Meta>
            <p className="text-[11px] text-muted-foreground pt-1">{t("createdBy")}: {t0.created_by_name} · {t0.created_at?.slice(0, 10)}</p>
            {canEdit && <Button className="w-full" onClick={save} data-testid="save-ticket"><Save className="w-4 h-4 mr-1.5" />{t("save")}</Button>}
          </div>
        </div>

        {/* Close-with-proof overlay */}
        {closing && (
          <div className="absolute inset-0 bg-background/95 flex items-center justify-center p-6 z-10">
            <div className="w-full max-w-md space-y-3 rounded-xl border border-border p-5 bg-card">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />{t("closeTicket")}</h3>
              <Textarea rows={2} placeholder={t("proofNote")} value={proof.note} onChange={(e) => setProof((p) => ({ ...p, note: e.target.value }))} data-testid="proof-note" />
              <UploadButton label={t("uploadProof")} accept="*" category="ticket-proof" testId="upload-proof" onUploaded={(d) => setProof((p) => ({ ...p, file_id: d.id, name: d.original_filename }))} />
              {proof.name && <p className="text-xs text-muted-foreground">✓ {proof.name}</p>}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setClosing(false)}>{t("cancel")}</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doClose} data-testid="confirm-close">{t("closeTicket")}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, children }) {
  return <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}

function personSelect(value, onChange, users, canEdit, testId) {
  return (
    <Select value={value || "none"} disabled={!canEdit} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">—</SelectItem>
        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
